import { z } from "zod";

/** Mirrors server rules in app/services/auth.py (server remains the authority). */
export const PASSWORD_MIN = 12;

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters.`)
  .max(128, "Use no more than 128 characters.");

const email = z
  .string()
  .trim()
  .min(1, "Enter your email address.")
  .email("Enter a valid email address.");

export const loginSchema = z.object({
  email,
  password: z.string().min(1, "Enter your password."),
});

export const mfaSchema = z.object({
  mfa_code: z.string().trim().min(6, "Enter the 6-digit code or a recovery code."),
});

export const registerSchema = z
  .object({
    first_name: z.string().trim().min(1, "Enter your first name.").max(100),
    last_name: z.string().trim().min(1, "Enter your last name.").max(100),
    email,
    phone: z.string().trim().max(40).optional(),
    password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((v) => v.password === v.confirm_password, {
    path: ["confirm_password"],
    message: "The passwords do not match.",
  })
  .refine((v) => v.password.toLowerCase() !== v.email.toLowerCase(), {
    path: ["password"],
    message: "Your password cannot be your email address.",
  });

export const emailOnlySchema = z.object({ email });

export const resetSchema = z
  .object({ password: passwordSchema, confirm_password: z.string() })
  .refine((v) => v.password === v.confirm_password, {
    path: ["confirm_password"],
    message: "The passwords do not match.",
  });

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, "Enter your current password."),
    new_password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ["confirm_password"],
    message: "The passwords do not match.",
  });

export type LoginValues = z.infer<typeof loginSchema>;
export type RegisterValues = z.infer<typeof registerSchema>;
